import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";
import { Box, Flex, Stack } from "@similarweb/ui-components/foundations";

import { AssetsService } from "app/services/AssetsService";

import { StyledFloatingIconPreviewBadge, StyledFloatingIconPreviewLogoWrapper } from "./styles";

const SI_LOGO_URL = "images/solution-switcher/sales-intelligence.svg";

const FloatingIconPreviewAddressBar = ({ isAnimated }: { isAnimated: boolean }) => {
    const { translate } = useTranslate();

    return (
        <Flex
            alignItems="center"
            background={colorsPalettes.carbon[100]}
            padding="4px 8px"
            borderRadius="4px 4px 0 0"
            borderBottom={`1px solid ${colorsPalettes.carbon[50]}`}
        >
            <Box
                fontVariant="queryBar1"
                color={colorsPalettes.carbon[200]}
                padding="2px 8px"
                border={`1px solid ${colorsPalettes.carbon[50]}`}
                borderRadius="4px"
                background={colorsPalettes.carbon[0]}
                whiteSpace="nowrap"
                textTransform="uppercase"
                letterSpacing="0.5px"
            >
                {translate("si.management.extension.settings.floating_icon.preview.label")}
            </Box>
            <Box
                flex="1"
                height="10px"
                borderRadius="3px"
                background={colorsPalettes.carbon[0]}
                border={`1px solid ${colorsPalettes.carbon[50]}`}
            />
            <Tooltip
                content={
                    <span style={{ whiteSpace: "nowrap" }}>
                        {translate("si.management.extension.settings.floating_icon.preview.tooltip")}
                    </span>
                }
                placement="bottom"
                open={isAnimated}
                enabled={isAnimated}
            >
                <StyledFloatingIconPreviewLogoWrapper $isAnimated={isAnimated}>
                    <img
                        src={AssetsService.assetUrl(SI_LOGO_URL)}
                        alt=""
                        width={12}
                        height={12}
                    />
                </StyledFloatingIconPreviewLogoWrapper>
            </Tooltip>
        </Flex>
    );
};

const FloatingIconPreviewSkeletonBars = () => (
    <Stack
        gap="6px"
        flex="1"
        justifyContent="center"
        minWidth="0"
        padding="10px 14px 24px"
    >
        <Box
            height="6px"
            borderRadius="3px"
            background={colorsPalettes.carbon[50]}
            width="100%"
            margin="0 0 4px 0"
        />
        <Box
            height="4px"
            borderRadius="2px"
            background={colorsPalettes.carbon[50]}
            width="60%"
        />
        <Box
            height="4px"
            borderRadius="2px"
            background={colorsPalettes.carbon[50]}
            width="80%"
        />
        <Box
            height="4px"
            borderRadius="2px"
            background={colorsPalettes.carbon[50]}
            width="45%"
        />
    </Stack>
);

const FloatingIconPreviewFloatingBadge = ({ isShown }: { isShown: boolean }) => (
    <StyledFloatingIconPreviewBadge $isShown={isShown}>
        <img
            src={AssetsService.assetUrl(SI_LOGO_URL)}
            alt=""
            width={16}
            height={16}
        />
    </StyledFloatingIconPreviewBadge>
);

type FloatingIconPreviewProps = {
    isSelected: boolean;
};

export const FloatingIconPreview = ({ isSelected }: FloatingIconPreviewProps) => {
    return (
        <Box
            position="relative"
            overflow="hidden"
            data-automation="sales-intelligence-extension-floating-preview"
        >
            <Box
                borderRadius="10px"
                border={`1px solid ${colorsPalettes.carbon[50]}`}
                background={colorsPalettes.carbon[25]}
            >
                <FloatingIconPreviewAddressBar isAnimated={!isSelected} />
                <FloatingIconPreviewSkeletonBars />
            </Box>
            <FloatingIconPreviewFloatingBadge isShown={isSelected} />
        </Box>
    );
};
